import { CartUpdatedEvent } from '@theme/events';

const STORAGE_KEY_END = 'fullstack:cart-reservation-timer:end-timestamp';
const STORAGE_KEY_SIGNATURE = 'fullstack:cart-reservation-timer:cart-signature';

class CartReservationTimer extends HTMLElement {
  constructor() {
    super();

    this.timeEl = null;
    this.activeEl = null;
    this.expiredEl = null;
    this.intervalId = null;
    this.endTimestamp = null;
    this.durationMs = 0;
    this.cartSignature = '';
    this.clearCartOnExpire = true;
    this.isClearingCart = false;

    this.storageHandler = this.#handleStorageChange.bind(this);
  }

  connectedCallback() {
    this.activeEl = this.querySelector('[data-ref="active"]');
    this.expiredEl = this.querySelector('[data-ref="expired"]');
    this.timeEl = this.querySelector('[data-ref="time"]');

    this.cartSignature = this.dataset.cartSignature || '';
    const durationMinutes = parseInt(this.dataset.durationMinutes, 10) || 15;
    this.durationMs = durationMinutes * 60 * 1000;
    this.clearCartOnExpire = this.dataset.clearCartOnExpire !== 'false';
    this.isDesignMode = window.Shopify?.designMode === true;

    if (!this.cartSignature) return;

    this.#initTimer();

    if (!this.isDesignMode) window.addEventListener('storage', this.storageHandler);
  }

  disconnectedCallback() {
    this.#clearInterval();
    if (!this.isDesignMode) window.removeEventListener('storage', this.storageHandler);
  }

  #initTimer() {
    if (this.isDesignMode) {
      this.endTimestamp = Date.now() + this.durationMs;
      this.#showActive();
      this.#startInterval();
      return;
    }

    const storedSignature = localStorage.getItem(STORAGE_KEY_SIGNATURE);
    const storedEnd = parseInt(localStorage.getItem(STORAGE_KEY_END), 10);
    const now = Date.now();

    const cartChanged = storedSignature !== this.cartSignature;
    const hasValidStored = !isNaN(storedEnd) && storedEnd > now;

    if (cartChanged) {
      this.#startNewTimer();
      return;
    }

    if (hasValidStored) {
      this.endTimestamp = storedEnd;
      this.#showActive();
      this.#startInterval();
      return;
    }

    this.#handleExpired();
  }

  #startNewTimer() {
    this.endTimestamp = Date.now() + this.durationMs;
    localStorage.setItem(STORAGE_KEY_END, String(this.endTimestamp));
    localStorage.setItem(STORAGE_KEY_SIGNATURE, this.cartSignature);
    this.#showActive();
    this.#startInterval();
  }

  #startInterval() {
    this.#clearInterval();
    this.#updateDisplay();
    this.intervalId = setInterval(() => this.#updateDisplay(), 1000);
  }

  #updateDisplay() {
    const remaining = this.endTimestamp - Date.now();

    if (remaining <= 0) {
      this.#handleExpired();
      return;
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    const formatted = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    if (this.timeEl) this.timeEl.textContent = formatted;
  }

  #handleExpired() {
    this.#clearInterval();
    localStorage.removeItem(STORAGE_KEY_END);
    localStorage.removeItem(STORAGE_KEY_SIGNATURE);

    if (this.clearCartOnExpire) {
      this.#showCountdownAtZero();
      this.#clearCart();
      return;
    }

    this.#showExpired();
  }

  #showCountdownAtZero() {
    if (this.timeEl) this.timeEl.textContent = '00:00';
    this.#showActive();
  }

  #showActive() {
    this.activeEl?.classList.remove('hidden');
    this.expiredEl?.classList.add('hidden');
  }

  #showExpired() {
    this.activeEl?.classList.add('hidden');
    this.expiredEl?.classList.remove('hidden');
  }

  async #clearCart() {
    if (this.isDesignMode || this.isClearingCart) return;

    this.isClearingCart = true;

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(`${Theme.routes.cart_url}/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return;

      const data = await response.json();

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: data.item_count ?? 0,
            source: 'cart-reservation-timer',
            sections: data.sections,
          },
        ),
      );

      const wrapper = this.closest('.cart-reservation-timer');
      if (wrapper) wrapper.dataset.cartEmpty = 'true';
    } catch (error) {
      console.error(error);
    } finally {
      this.isClearingCart = false;
    }
  }

  #clearInterval() {
    if (!this.intervalId) return;
    clearInterval(this.intervalId);
    this.intervalId = null;
  }

  #handleStorageChange(event) {
    if (event.key !== STORAGE_KEY_END && event.key !== STORAGE_KEY_SIGNATURE) return;
    this.#initTimer();
  }
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}
